import React, { useEffect, useMemo, useState } from 'react';
import { Circle, CircleCheck, Download, KeyRound, LoaderCircle } from 'lucide-react';
import { AuthHeading } from './AuthShell.jsx';
import { OtpEmailField, useOtp } from '../components/Otp.jsx';
import { PhraseGrid } from '../components/Phrase.jsx';
import { PasswordStrength } from '../components/ui.jsx';
import { useToast } from '../components/ui.jsx';
import { downloadBlob, generatePhrase, passwordScore, recoveryKitBlob } from '../lib/recovery.js';

const STEPS = [
  ['Generating ML-KEM-768 encryption key', 'kem'],
  ['Generating ML-DSA-65 signing key', 'sig'],
  ['Sealing private keys with your password', 'seal'],
  ['Publishing public key to the directory', 'publish'],
];

/**
 * Registration. Step 1 collects identity + optional verified recovery email.
 * Step 2 runs key generation (crypto-service) and shows the recovery phrase.
 * `registration` comes from GET /auth/v1/registration — when disabled by the
 * admin we show the "contact your administrator" notice instead.
 */
export function Register({ api, crypto, registration, onDone, go }) {
  const flash = useToast();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [local, setLocal] = useState('');
  const [domain, setDomain] = useState(registration?.domains?.[0] || 'kryptamail.test');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [terms, setTerms] = useState(false);
  const [recEmail, setRecEmail] = useState('');
  const otp = useOtp(api, 'register');
  const [progress, setProgress] = useState(0);
  const [phrase] = useState(() => generatePhrase());
  const [saved, setSaved] = useState(false);
  const [fingerprint, setFingerprint] = useState('');
  const email = `${local}@${domain}`;

  const score = passwordScore(pw);
  const valid = name.trim() && local.trim() && score >= 3 && pw === pw2 && terms && (!registration?.requireRecoveryEmail || otp.verified);

  if (registration && registration.enabled === false) {
    return <div className="km-auth-form"><AuthHeading title="Registration is closed" sub="This server only lets administrators create mailboxes. Ask your administrator for an account." /><button className="km-btn-outline" onClick={() => go('login')}>Back to sign in</button></div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { flash('Complete the form to continue'); return; }
    if (recEmail.trim() && !otp.verified) { flash('Verify your recovery email or clear the field'); return; }
    setStep(2);
    try {
      // Real flow: keygen happens client-side (WebCrypto/liboqs-wasm), then the
      // public halves are registered. crypto-service exposes the primitives;
      // here we drive the UI steps as each completes.
      setProgress(1); const kem = await crypto.generateKem?.() ?? await wait(600);
      setProgress(2); const sig = await crypto.generateSig?.() ?? await wait(600);
      setProgress(3); await wait(500); // seal with KDF(password) + KDF(phrase)
      setProgress(4);
      const r = await api.register({ name: name.trim(), email, password: pw, recoveryToken: otp.token || undefined, recoveryEmail: otp.verified ? recEmail.trim() : undefined, kemPublicKey: kem?.publicKey, sigPublicKey: sig?.publicKey });
      setFingerprint(r.fingerprint || '');
      setProgress(5);
    } catch (err) { flash(err.message || 'Registration failed'); setStep(1); setProgress(0); }
  };

  if (step === 2) {
    const done = progress >= 5;
    return (
      <div className="km-auth-form">
        <AuthHeading title={done ? 'Your identity is ready' : 'Generating your post-quantum identity'} sub={done ? `${email} can now receive end-to-end encrypted mail.` : 'This runs on your device. It usually takes a few seconds.'} />
        <div className="km-card" style={{ padding: '6px 0' }}>
          {STEPS.map(([label], i) => { const d = progress > i + 1 || done, cur = progress === i + 1 && !done; return (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', fontSize: 14, color: d || cur ? 'var(--km-text)' : 'var(--km-faint)' }}>
              {d ? <CircleCheck size={17} color="var(--km-ok)" /> : cur ? <LoaderCircle size={17} color="var(--km-accent)" style={{ animation: 'km-spin 1s linear infinite' }} /> : <Circle size={17} color="var(--km-line2)" />}
              <span style={{ flex: 1 }}>{label}</span><span className="km-mono km-faint" style={{ fontSize: 12 }}>{d ? 'done' : cur ? 'working' : ''}</span>
            </div>); })}
        </div>
        {done && (<>
          <div className="km-card" style={{ background: 'var(--km-panel2)', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span className="km-kicker">Your key fingerprint</span>
            <span className="km-mono" style={{ fontSize: 15, lineHeight: 1.6, letterSpacing: '.04em', fontWeight: 500, color: fingerprint ? undefined : 'var(--km-faint)' }}>{fingerprint || 'Pending · available once your key is published'}</span>
            <span className="km-muted" style={{ fontSize: 13 }}>Share this with contacts so they can verify it is really you.</span>
          </div>
          <div className="km-card" style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <PhraseGrid words={phrase} onCopy={() => { navigator.clipboard?.writeText(phrase.join(' ')); flash('Recovery phrase copied. Clear your clipboard afterwards.'); }} />
            <span className="km-muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>These words can unseal your keys if you forget your password. Anyone holding them can read your mail. Write them down and keep them offline.</span>
            <label className="km-check"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />I have written down my recovery phrase</label>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="km-btn-outline" style={{ flex: 1, padding: 12, fontSize: 14 }} onClick={() => downloadBlob(recoveryKitBlob({ email, phrase, fingerprint }), 'kryptamail-recovery-kit.txt')}><Download size={16} />Download recovery kit</button>
            <button className="km-btn" style={{ flex: 1, padding: 12, fontSize: 14 }} disabled={!saved} onClick={() => onDone({ email, password: pw, name: name.trim(), phrase })}>Continue to inbox</button>
          </div>
        </>)}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="km-auth-form" style={{ width: 440 }}>
      <AuthHeading title="Create your account" sub="A post-quantum identity key is generated on this device. The server never sees your private key." />
      <label className="km-field">Full name<input className="km-input" required placeholder="Alice Wijaya" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="km-field">Email address
        <div className="km-join">
          <input required placeholder="alice" value={local} onChange={(e) => setLocal(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))} />
          <select value={domain} onChange={(e) => setDomain(e.target.value)}>{(registration?.domains || ['kryptamail.test']).map((d) => <option key={d} value={d}>@{d}</option>)}</select>
        </div>
      </label>
      <label className="km-field">Password<input className="km-input" type="password" autoComplete="new-password" placeholder="At least 12 characters" value={pw} onChange={(e) => setPw(e.target.value)} /><PasswordStrength score={score} pw={pw} /></label>
      <label className="km-field">Confirm password<input className={`km-input ${pw2 && pw2 !== pw ? 'bad' : ''}`} type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></label>
      <div className="km-card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 9 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: 13.5, fontWeight: 600 }}>Recovery email <span className="km-faint" style={{ fontWeight: 400 }}>· {registration?.requireRecoveryEmail ? 'required' : 'optional'}</span></span>{otp.verified && <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--km-ok)' }}><CircleCheck size={14} />Verified</span>}</div>
        <span className="km-muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>Lets you find your address and reset your password later. Mail content is never sent there.</span>
        <OtpEmailField otp={otp} email={recEmail} onEmail={setRecEmail} />
      </div>
      <label className="km-check top"><input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} /><span>I understand that if I lose my password and recovery kit, encrypted mail cannot be recovered.</span></label>
      <button type="submit" className="km-btn km-btn-lg" aria-disabled={!valid}><KeyRound size={16} />Generate keys &amp; create account</button>
      <div className="km-muted" style={{ textAlign: 'center', fontSize: 13.5 }}>Already have an account? <a href="#login" onClick={(e) => { e.preventDefault(); go('login'); }}>Sign in</a></div>
    </form>
  );
}

const wait = (ms) => new Promise((r) => setTimeout(() => r(null), ms));
